/**
 * T4 (auditoría L-03, TyC 3.3 §8.8) — textos de cobranza, puros (los prueba vitest).
 * Un cobro de suscripción rechazado NO suspende: Stripe reintenta y el acceso sigue.
 * La suspensión llega solo cuando se agotan los reintentos.
 */
export interface TenantBillingState {
  pastDueSince: string | null;
  failedAttempts: number;
  suspendedAt: string | null;
  exportUntil: string | null;
}

const fmt = (iso: string) => new Date(iso).toLocaleDateString('es-MX');

/** Aviso dentro de la app mientras hay un cobro rechazado sin resolver (acceso intacto). */
export function pastDueBannerText(b: TenantBillingState | undefined | null, isAdmin: boolean): string | null {
  if (!b?.pastDueSince) return null;
  const attempt = b.failedAttempts > 0 ? ` (intento ${b.failedAttempts})` : '';
  return isAdmin
    ? `No pudimos cobrar tu suscripción${attempt}. Tu acceso sigue activo; actualiza tu método de pago en Configuración → Cobros y pagos → Método de pago.`
    : `Hay un pago pendiente de la suscripción. Pide al administrador que actualice el método de pago.`;
}

/** Ventana de exportación (TyC §15) tras la suspensión. */
export function exportNoteText(b: TenantBillingState | undefined | null): string {
  return b?.exportUntil && new Date(b.exportUntil).getTime() > Date.now()
    ? ` Puedes descargar tus datos hasta el ${fmt(b.exportUntil)}.`
    : '';
}

/** Copy de la pantalla de bloqueo: prueba terminada vs suscripción suspendida por cobranza. */
export function blockedScreenCopy(b: TenantBillingState | undefined | null): {
  label: string; title: string; detail: string;
} {
  if (b?.suspendedAt) {
    const exportNote = exportNoteText(b);
    return {
      label: 'Suscripción suspendida',
      title: 'Tu suscripción está suspendida',
      detail: `No pudimos cobrar tu suscripción después de varios intentos. Actualiza tu método de pago para reactivarla.${exportNote}`,
    };
  }
  return {
    label: 'Trial expirado',
    title: 'Tu prueba de 14 días terminó',
    detail: 'Activa tu plan para recuperar el acceso.',
  };
}

/** Contacto de FluidSol publicado en el Aviso de Privacidad (auditoría L-06). */
export const FLUIDSOL_CONTACT_EMAIL = 'contacto@fluidsol.mx';
export const FLUIDSOL_SUPPORT_EMAIL = 'soporte@fluidsol.mx';

/** T4 (opción A, 09/10) — GET /billing/reactivation: qué se cobra para reactivar. */
export interface ReactivationInfo {
  status: string | null;
  needed: boolean;
  /** La vencida ya se pagó en la página de Stripe y la suscripción sigue unpaid. */
  overduePaid: boolean;
  toPay: Array<{ id: string; number: string | null; amountDue: number; periodStart: number | null; periodEnd: number | null }>;
  voided: number;
  totalCents: number;
  currency: string;
  card: { brand: string; last4: string; paymentMethodId: string } | null;
}

/** Con una suscripción con adeudo (past_due/unpaid) NO se ofrece Checkout: abriría una
 *  segunda suscripción (M-09). Se ofrece "Pagar adeudo y reactivar". */
export const hasDebt = (r: ReactivationInfo | null | undefined): boolean =>
  !!r && (r.status === 'past_due' || r.status === 'unpaid');

/** Copy de la pantalla de suspensión según el estado de la reactivación. */
export function reactivationCopy(r: ReactivationInfo | null | undefined): { title: string; detail: string } | null {
  if (!r?.needed) return null;
  if (r.overduePaid) {
    return {
      title: 'Falta un paso para reactivar',
      detail: 'Recibimos el pago de la factura vencida, pero tu suscripción sigue suspendida: falta cubrir el periodo en curso. Paga el adeudo para reactivarla.',
    };
  }
  return {
    title: 'Tu suscripción está suspendida',
    detail: 'No pudimos cobrar tu suscripción después de varios intentos. Paga el adeudo para reactivarla.',
  };
}

/** Rango de periodo de una factura (epoch s → dd/mm/aaaa, UTC-6 fijo como los correos). */
export function periodLabel(start: number | null, end: number | null): string {
  const f = (s: number) => {
    const d = new Date(s * 1000 - 6 * 3600_000);
    return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
  };
  return start && end ? `${f(start)} – ${f(end)}` : '';
}
